import { Link } from '@/i18n/navigation'
import { ImageBlock } from './ImageBlock'
import { ImageBlock as ImageBlockType } from '@/payload-types'
import { ComponentProps } from 'react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button-variants'

export type ListCardType = {
  image?: ImageBlockType
  title: string
  campaignTitle?: string
  description: string | null | undefined
  date?: string
  link: ComponentProps<typeof Link>['href']
  className?: string
  priority?: boolean
  loading?: 'lazy' | 'eager'
  // White card with content left, image right from sm and up
  horizontal?: boolean
  buttonLabel?: string
}

// Cards render in 2–3 column grids or carousels and are capped by max-w-2xl,
// so the image never exceeds 672px CSS width.
const cardSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 672px'

const ListCard = ({
  image,
  title,
  description,
  date,
  link,
  className,
  priority,
  loading,
  horizontal,
  buttonLabel,
}: ListCardType) => {
  return (
    <Link href={link} className='duration-300 hover:opacity-60'>
      <div
        className={cn(
          { 'rounded-2xl bg-white p-4 sm:flex sm:flex-row-reverse sm:gap-6': horizontal },
          className,
        )}
      >
        <div
          className={cn('relative w-full max-w-2xl overflow-hidden rounded-lg', {
            'sm:w-1/2 sm:shrink-0': horizontal,
          })}
        >
          <ImageBlock
            ratio={16 / 9}
            imageBlock={image}
            fill
            imgClassName='object-cover'
            size={cardSizes}
            priority={priority}
            loading={loading}
          />
        </div>

        <div className={cn('pt-2', { 'flex flex-1 flex-col sm:pt-0': horizontal })}>
          {date && <p className='text-sm text-stone-500'>{date}</p>}
          <h3 className='text-xl'>{title}</h3>
          <p className='line-clamp-3 text-stone-500'>{description}</p>
          {buttonLabel && (
            // span, not <button>: the whole card is already a link
            <span
              className={buttonVariants({
                variant: 'secondary',
                className: 'mt-4 self-start sm:mt-auto',
              })}
            >
              {buttonLabel}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export default ListCard
